import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import {
  dateFormatter,
  dateTimeFormatter,
  getFormattedRuPhone,
} from 'core/utils/commonUtils';
import useOrderMangoCalls from 'hooks/swr/useOrderMangoCalls';
import useResponsive from 'hooks/useResponsive';
import { isEmpty } from 'lodash';
import type { MangoCallInfo } from 'models/mangoCall';
import type { GridColDef } from '@mui/x-data-grid';
import type { FC } from 'react';

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

const pulseAnimation = {
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
    '70%': { boxShadow: '0 0 0 8px rgba(25, 118, 210, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
  },
  animation: 'pulse 1.5s ease-out infinite',
  borderRadius: '50%',
};

function ParticipantInfo({
  call,
  side,
}: {
  call: MangoCallInfo;
  side: 'from' | 'to';
}): JSX.Element {
  const phone = getFormattedRuPhone(side === 'from' ? call.fromNumber : call.toNumber);
  const name =
    side === 'from'
      ? call.fromUser?.fullName ?? call.fromClient?.fullName
      : call.toUser?.fullName ?? call.toClient?.fullName;

  return (
    <Box>
      <Typography variant='body2' lineHeight={1.3}>
        {name ?? phone}
      </Typography>
      {name != null && (
        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          lineHeight={1.3}>
          {phone}
        </Typography>
      )}
    </Box>
  );
}

interface OrderMangoCallsProps {
  orderId: number;
  selectedCall: MangoCallInfo | null;
  isPlaying: boolean;
  onSelectCall: (call: MangoCallInfo | null) => void;
  onTogglePlay: () => void;
}

const OrderMangoCalls: FC<OrderMangoCallsProps> = ({
  orderId,
  selectedCall,
  isPlaying,
  onSelectCall,
  onTogglePlay,
}) => {
  const { data: calls, isLoading } = useOrderMangoCalls(orderId, orderId > 0);
  const isSmScreen = useResponsive('down', 'sm');

  const items = calls ?? [];

  const columns: Array<GridColDef<MangoCallInfo>> = [
    {
      field: 'dateOfCall',
      headerName: 'Дата звонка',
      type: 'dateTime',
      minWidth: 170,
      valueFormatter: (value: Date) => dateTimeFormatter.format(value),
    },
    {
      field: 'fromNumber',
      headerName: 'От кого',
      flex: 0.5,
      minWidth: 160,
      renderCell: (params) => <ParticipantInfo call={params.row} side='from' />,
    },
    {
      field: 'toNumber',
      headerName: 'Кому',
      flex: 0.5,
      minWidth: 160,
      renderCell: (params) => <ParticipantInfo call={params.row} side='to' />,
    },
    {
      field: 'duration',
      headerName: 'Длительность',
      minWidth: 120,
      valueFormatter: (value: number) => formatDuration(value),
    },
    {
      field: 'recordUrl',
      headerName: 'Запись',
      minWidth: 80,
      sortable: false,
      renderCell: (params) => {
        const isSelected = selectedCall?.id === params.row.id;
        const isRowPlaying = isSelected && isPlaying;
        return !isEmpty(params.value) ? (
          <IconButton
            size='small'
            onClick={() => {
              if (isSelected) {
                onTogglePlay();
              } else {
                onSelectCall(params.row);
              }
            }}
            color={isSelected ? 'primary' : 'default'}
            sx={isRowPlaying ? pulseAnimation : undefined}>
            {isRowPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        ) : null;
      },
    },
  ];

  if (isSmScreen) {
    return (
      <List disablePadding>
        {items.map((call) => {
          const isSelected = selectedCall?.id === call.id;
          const isRowPlaying = isSelected && isPlaying;
          return (
            <ListItem
              key={call.id}
              divider
              secondaryAction={
                !isEmpty(call.recordUrl) ? (
                  <IconButton
                    size='small'
                    onClick={() => {
                      if (isSelected) {
                        onTogglePlay();
                      } else {
                        onSelectCall(call);
                      }
                    }}
                    color={isSelected ? 'primary' : 'default'}
                    sx={isRowPlaying ? pulseAnimation : undefined}>
                    {isRowPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                ) : undefined
              }>
              <ListItemText
                disableTypography
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    <ParticipantInfo call={call} side='from' />
                    <ArrowForwardIcon
                      sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                    />
                    <ParticipantInfo call={call} side='to' />
                  </Box>
                }
                secondary={
                  <Typography variant='caption' color='text.secondary'>
                    {`${dateFormatter.format(call.dateOfCall)} · ${formatDuration(
                      call.duration
                    )}`}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    );
  }

  return (
    <DataGrid
      rows={items}
      columns={columns}
      loading={isLoading}
      getRowHeight={() => 'auto'}
      sx={{
        [`& .${gridClasses.cell}`]: {
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
        },
      }}
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      disableColumnSorting
      hideFooter
    />
  );
};

export default OrderMangoCalls;
